import { IconButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/IconButton';
import { ConfigurationList } from '@lmt/unicorn-shared/app/components/configurator/configuration-list/ConfigurationList';
import {
  isMiniscreen,
  isStartMiniscreenOfflineModeEnabled,
  isStartMiniscreenOnlineModeEnabled,
  MiniscreenMode,
  openWithMiniscreen,
} from '@lmt/unicorn-shared/app/components/patients/common';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { UIConfigurationsActions } from '@lmt/unicorn-shared/app/features/ui-configurations/reducer';
import { getUIConfigurations } from '@lmt/unicorn-shared/app/features/ui-configurations/selector';
import {
  useUniDispatch,
  useUniSelector,
} from '@lmt/unicorn-shared/app/store/types';
import { isElectron } from '@lmt/unicorn-shared/app/helpers/common';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { PatientProfileContext, PatientTab } from '../types';
import { PatientNavTab } from './components/PatientNavtab';

import styles from './PatientNavigationBar.module.scss';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { AppointmentStatusCodes, QueryFactory } from '@lmt/shared-types';
import { MiniscreenModeIcon } from '../../../common/icons/miniscreen/MiniscreenModeIcon';
import { useContextSelector } from 'use-context-selector';
import { useModal } from '../../../common/ui-kit/modal/useModal';
import { useTranslation } from 'react-i18next';
import { useEventCallback } from '@mui/material';
import { useOnlineMeasurementSettings } from '../../list/hooks/useOnlineMeasurementSettings';

interface Props {
  patientId: number;
  currentTab: string;
  patientOverviewIsEdit: boolean;
  setPatientOverviewEdit: (value: boolean) => void;
  editableConfigId?: number;
  setEditableConfigId: (value?: number) => void;
}

const patientTabs = Object.values(PatientTab);

export const PatientNavigationBar: FC<Props> = (props) => {
  const { patientId, currentTab, editableConfigId, setEditableConfigId } =
    props;
  const claims = useClaims();
  const tabs = useTabs();
  const i18next = useScopedTranslation('patients.list.options');
  const i18n = useTranslation();
  const dispatch = useUniDispatch();
  const modals = useModal();

  const hasUnsavedChanges = useContextSelector(
    PatientProfileContext,
    (v) => v.hasUnsavedChanges,
  );

  const cancelEditing = useContextSelector(
    PatientProfileContext,
    (v) => v.cancelEditing,
  );

  const clearUnsavedChanges = useContextSelector(
    PatientProfileContext,
    (v) => v.clearUnsavedChanges,
  );

  const confirmUnsavedChanges = useEventCallback(async () => {
    if (hasUnsavedChanges) {
      const isConfirmed = await modals.showConfirm({
        title: i18n.t('patients.unsaved_changes_confirmation.title'),
        text: i18n.t('patients.unsaved_changes_confirmation.text_leave_page'),
        okButtonText: i18n.t('patients.unsaved_changes_confirmation.ok_text'),
        cancelButtonText: i18n.t(
          'patients.unsaved_changes_confirmation.cancel_text',
        ),
      });
      if (!isConfirmed) {
        return false;
      }
    }
    cancelEditing(true);
    clearUnsavedChanges();

    return true;
  });

  const openPatientTab = useCallback(
    async (tab: string, replace = false) => {
      const isConfirmed = await confirmUnsavedChanges();
      if (!isConfirmed) {
        return;
      }
      tabs.changeTabPath(
        pages.patientProfileWithTab.link({ patientId, tabName: tab }),
        replace,
      );
    },
    [hasUnsavedChanges],
  );
  const onCreateConfig = useEventCallback(async () => {
    const isConfirmed = await confirmUnsavedChanges();
    if (!isConfirmed) {
      return;
    }
    // we need to set ConfigId before switching to edit mode to prevent #65359: "Page was not found" is displayed when trying to create a new overview config
    setEditableConfigId(undefined);
    props.setPatientOverviewEdit(true);
  });

  const onEditConfig = useEventCallback(async (configId: number) => {
    const isConfirmed = await confirmUnsavedChanges();
    if (!isConfirmed) {
      return;
    }
    // we need to set ConfigId before switching to edit mode to prevent #65359: "Page was not found" is displayed when trying to create a new overview config
    setEditableConfigId(configId);
    props.setPatientOverviewEdit(true);
  });

  const defaultConfigId =
    useUniSelector(getUIConfigurations).patientOverviewUIConfigurationId;
  const configurationsQuery =
    QueryFactory.UIConfigurationQuery.useGetPatientOverviewConfigurationsQuery();

  const changeConfig = useCallback(
    async (configId: number) => {
      const isConfirmed = await confirmUnsavedChanges();
      if (!isConfirmed) {
        return;
      }
      dispatch(
        UIConfigurationsActions.patientOverviewUIConfigurationIdChanged(
          configId,
        ),
      );
    },
    [dispatch, hasUnsavedChanges],
  );

  useEffect(() => {
    if (currentTab === '') {
      openPatientTab(PatientTab.Overview, true);
    }
  }, [currentTab]);

  const visitsQuery = QueryFactory.AppointmentEventsQuery.useSearchVisitsQuery({
    patientId: patientId,
  });
  const { currentRoom, isOnlineMeasurementsDisabled } =
    useOnlineMeasurementSettings();

  const isMiniscreenOnlineModeEnabled = useMemo(
    () =>
      isMiniscreen() &&
      (!isOnlineMeasurementsDisabled ||
        visitsQuery.data?.some(
          (x) =>
            x.end > new Date() &&
            (!currentRoom || x.resourceIds.includes(currentRoom)) &&
            x.statusId !== AppointmentStatusCodes.Canceled,
        )),
    [visitsQuery.data, currentRoom, isOnlineMeasurementsDisabled],
  );

  return (
    <div className={styles.container}>
      <div className={styles.tabs}>
        {patientTabs.map((tab) => (
          <PatientNavTab
            key={tab}
            isSelected={tab === currentTab}
            title={tab}
            onClick={openPatientTab}
          />
        ))}
      </div>
      <div className={styles.iconButtons}>
        {isElectron() &&
          isMiniscreen() &&
          claims.permissions.importMeasurements &&
          isStartMiniscreenOnlineModeEnabled() && (
            <IconButton
              color="blue"
              disabled={!isMiniscreenOnlineModeEnabled}
              className={styles.iconButton}
              iconComponent={
                <MiniscreenModeIcon
                  disabled={!isMiniscreenOnlineModeEnabled}
                  type="online"
                  className={styles.icon}
                />
              }
              onClick={() =>
                openWithMiniscreen({ patientId, mode: MiniscreenMode.Online })
              }
              disabledTooltip={i18next.t('miniscreen_online_disabled')}
              tooltip={i18next.t('start_miniscreen_online')}
            />
          )}
        {isElectron() &&
          isMiniscreen() &&
          claims.permissions.importMeasurements &&
          isStartMiniscreenOfflineModeEnabled() && (
            <IconButton
              color="blue"
              disabled={!isMiniscreen()}
              className={styles.iconButton}
              iconComponent={
                <MiniscreenModeIcon type="offline" className={styles.icon} />
              }
              onClick={() =>
                openWithMiniscreen({ patientId, mode: MiniscreenMode.Offline })
              }
              tooltip={i18next.t('start_miniscreen_offline')}
            />
          )}
        {currentTab === PatientTab.Overview && !props.patientOverviewIsEdit && (
          <ConfigurationList
            currentConfigurationId={defaultConfigId ?? undefined}
            configurations={configurationsQuery.data ?? []}
            onConfigurationChange={changeConfig}
            editConfig={onEditConfig}
            createConfig={onCreateConfig}
            isSharedEditAllowed={
              claims.permissions.editTenantPatientOverviewUIConfiguration
            }
            isPersonalEditAllowed={true}
            testId="patient-overview-configurations"
            configName="patient_overview"
          />
        )}
      </div>
    </div>
  );
};
